// src/components/AddCommissionModal.js
import React, { useState, useEffect } from 'react';
import { useTecnicos } from '../hooks/useTecnicos';

const TIPOS_COMISION = [
  { tipo: 'MANTENIMIENTO_AEROTERMIA', nombre: 'Mantenimiento Aerotermia', descripcion: 'Venta mantenimiento de aire cuando haces aerotermia', importe: 5.0 },
  { tipo: 'EXTENSION_GARANTIA',       nombre: 'Extensión Garantía',       descripcion: 'Venta extensión garantía', importe: 5.0 },
  { tipo: 'FILTRO_POLIFOSFATOS',      nombre: 'Filtro Polifosfatos',      descripcion: 'Venta filtro polifosfatos', importe: 2.5 },
  { tipo: 'MAS_INCIDENCIAS',          nombre: 'Más Incidencias',          descripcion: 'Técnico con más incidencias resueltas al mes', importe: 25.0 },
  { tipo: 'VENTA_EQUIPO_NUEVO',       nombre: 'Venta Equipo Nuevo',       descripcion: 'Venta equipo nuevo (aire o aerotermia)', importe: 25.0 },
  { tipo: 'MEJOR_VALORADO',           nombre: 'Mejor Valorado',           descripcion: 'Técnico mejor valorado', importe: 20.0 },
];

const hoyISO = () => new Date().toISOString().split('T')[0];

const AddCommissionModal = ({ isOpen, onClose, onSubmit, initialValues }) => {
  const [formData, setFormData] = useState({
    employeeId: '', tipo: '', descripcion: '', importe: '', workorderId: '', fechaCreacion: hoyISO(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { tecnicos } = useTecnicos();

  useEffect(() => {
    if (!isOpen) return;
    if (initialValues) {
      const t = initialValues.tipo ? TIPOS_COMISION.find(x => x.tipo === initialValues.tipo) : null;
      setFormData({
        employeeId:    String(initialValues.employeeId || ''),
        tipo:          initialValues.tipo          || '',
        descripcion:   t ? t.descripcion           : (initialValues.descripcion || ''),
        importe:       t ? t.importe.toString()    : (initialValues.importe     || ''),
        workorderId:   initialValues.workorderId   || '',
        fechaCreacion: initialValues.fechaCreacion || hoyISO(),
      });
    } else {
      setFormData({ employeeId: '', tipo: '', descripcion: '', importe: '', workorderId: '', fechaCreacion: hoyISO() });
    }
    setError('');
  }, [isOpen, initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tipo') {
      const t = TIPOS_COMISION.find(x => x.tipo === value);
      setFormData(prev => ({
        ...prev, tipo: value,
        descripcion: t ? t.descripcion : prev.descripcion,
        importe:     t ? t.importe.toString() : prev.importe,
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employeeId) { setError('Debes seleccionar un técnico'); return; }
    if (!formData.tipo)       { setError('Debes seleccionar un tipo de comisión'); return; }
    if (!formData.importe || parseFloat(formData.importe) <= 0) { setError('Debes ingresar un importe válido'); return; }
    if (!formData.workorderId.trim()) { setError('El número de orden es obligatorio'); return; }

    setSubmitting(true); setError('');
    try {
      const tipo = TIPOS_COMISION.find(t => t.tipo === formData.tipo);
      await onSubmit({
        employeeId:    parseInt(formData.employeeId),
        employeeName:  tecnicos.find(t => String(t.employeeId) === String(formData.employeeId))?.nombre || '',
        tipo:          formData.tipo,
        tipoString:    tipo ? tipo.nombre : formData.tipo,
        descripcion:   formData.descripcion || 'Sin descripción',
        importe:       parseFloat(formData.importe),
        workorderId:   formData.workorderId || null,
        fechaCreacion: formData.fechaCreacion || hoyISO(),
      });
      setFormData({ employeeId: '', tipo: '', descripcion: '', importe: '', workorderId: '', fechaCreacion: hoyISO() });
      onClose();
    } catch (err) {
      setError('Error al crear la comisión: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 p-5 rounded-t-2xl flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">➕ Nueva Comisión</h2>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Técnico */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Técnico *</label>
            <select name="employeeId" value={formData.employeeId} onChange={handleChange} required
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm">
              <option value="">Selecciona un técnico</option>
              {tecnicos.map(t => (
                <option key={t.id} value={t.employeeId}>{t.nombre}</option>
              ))}
            </select>
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tipo de Comisión *</label>
            <select name="tipo" value={formData.tipo} onChange={handleChange} required
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm">
              <option value="">Selecciona un tipo</option>
              {TIPOS_COMISION.map(t => (
                <option key={t.tipo} value={t.tipo}>{t.nombre} — €{t.importe.toFixed(2)}</option>
              ))}
            </select>
            {formData.tipo && (
              <p className="mt-1 text-xs text-gray-500">
                {TIPOS_COMISION.find(t => t.tipo === formData.tipo)?.descripcion}
              </p>
            )}
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Descripción</label>
            <textarea name="descripcion" value={formData.descripcion} onChange={handleChange} rows="2"
              placeholder="Detalles adicionales..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm resize-none" />
          </div>

          {/* Importe + Fecha en fila */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Importe (€) *</label>
              <input type="number" name="importe" value={formData.importe} onChange={handleChange}
                step="0.01" min="0" placeholder="0.00" required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fecha</label>
              <input type="date" name="fechaCreacion" value={formData.fechaCreacion} onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm" />
            </div>
          </div>

          {/* Orden de trabajo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Número de Orden *</label>
            <input type="text" name="workorderId" value={formData.workorderId} onChange={handleChange}
              placeholder="ID de orden" required
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm" />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 text-sm font-semibold disabled:opacity-50">
              {submitting
                ? <span className="flex items-center justify-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Guardando...
                  </span>
                : 'Crear Comisión'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCommissionModal;
